import apiClient from '@/lib/axios';
import type {
  ApiResponse,
  BlogPostListDto,
  BlogPostDetailDto,
  BlogPostsResponse,
  TagListDto,
  TagWithPostsResponse,
  AuthorListDto,
  AuthorWithPostsResponse,
  BlogArchiveDto,
  BlogSearchResponse,
  PublicCommentDto,
  CreateCommentRequest,
} from '@/types';

function appendLang(url: string, lang?: string): string {
  if (!lang || lang === 'en') return url;
  const separator = url.includes('?') ? '&' : '?';
  return `${url}${separator}lang=${lang}`;
}

export const blogApi = {
  // ─── Posts ───

  async getPosts(options?: {
    tag?: string;
    category?: string;
    author?: string;
    year?: number;
    month?: number;
    search?: string;
    featured?: boolean;
    page?: number;
    pageSize?: number;
    lang?: string;
  }): Promise<BlogPostsResponse> {
    const params = new URLSearchParams();
    if (options?.tag) params.append('tag', options.tag);
    if (options?.category) params.append('category', options.category);
    if (options?.author) params.append('author', options.author);
    if (options?.year) params.append('year', options.year.toString());
    if (options?.month) params.append('month', options.month.toString());
    if (options?.search) params.append('search', options.search);
    if (options?.featured !== undefined) params.append('featured', options.featured.toString());
    if (options?.page) params.append('page', options.page.toString());
    if (options?.pageSize) params.append('pageSize', options.pageSize.toString());
    if (options?.lang && options.lang !== 'en') params.append('lang', options.lang);

    const response = await apiClient.get<ApiResponse<BlogPostsResponse>>(
      `/cms/blog/posts?${params.toString()}`
    );
    return response.data.data;
  },

  async getPost(slug: string, lang?: string): Promise<BlogPostDetailDto> {
    const url = appendLang(`/cms/blog/posts/${slug}`, lang);
    const response = await apiClient.get<ApiResponse<BlogPostDetailDto>>(url);
    return response.data.data;
  },

  async getRelatedPosts(slug: string, count = 5): Promise<BlogPostListDto[]> {
    const response = await apiClient.get<ApiResponse<BlogPostListDto[]>>(
      `/cms/blog/posts/${slug}/related?count=${count}`
    );
    return response.data.data;
  },

  // ─── Comments ───

  async getComments(slug: string): Promise<PublicCommentDto[]> {
    const response = await apiClient.get<ApiResponse<PublicCommentDto[]>>(
      `/cms/blog/posts/${slug}/comments`
    );
    return response.data.data;
  },

  async submitComment(slug: string, data: CreateCommentRequest): Promise<number> {
    const response = await apiClient.post<ApiResponse<number>>(
      `/cms/blog/posts/${slug}/comments`,
      data
    );
    return response.data.data;
  },

  // ─── Tags ───

  async getTags(): Promise<TagListDto[]> {
    const response = await apiClient.get<ApiResponse<TagListDto[]>>('/cms/blog/tags');
    return response.data.data;
  },

  async getTagWithPosts(slug: string, options?: {
    page?: number;
    pageSize?: number;
  }): Promise<TagWithPostsResponse> {
    const params = new URLSearchParams();
    if (options?.page) params.append('page', options.page.toString());
    if (options?.pageSize) params.append('pageSize', options.pageSize.toString());

    const response = await apiClient.get<ApiResponse<TagWithPostsResponse>>(
      `/cms/blog/tags/${slug}?${params.toString()}`
    );
    return response.data.data;
  },

  // ─── Authors ───

  async getAuthors(): Promise<AuthorListDto[]> {
    const response = await apiClient.get<ApiResponse<AuthorListDto[]>>('/cms/blog/authors');
    return response.data.data;
  },

  async getAuthorWithPosts(slug: string, options?: {
    page?: number;
    pageSize?: number;
    lang?: string;
  }): Promise<AuthorWithPostsResponse> {
    const params = new URLSearchParams();
    if (options?.page) params.append('page', options.page.toString());
    if (options?.pageSize) params.append('pageSize', options.pageSize.toString());
    if (options?.lang && options.lang !== 'en') params.append('lang', options.lang);

    const response = await apiClient.get<ApiResponse<AuthorWithPostsResponse>>(
      `/cms/blog/authors/${slug}?${params.toString()}`
    );
    return response.data.data;
  },

  // ─── Archive ───

  async getArchive(): Promise<BlogArchiveDto[]> {
    const response = await apiClient.get<ApiResponse<BlogArchiveDto[]>>('/cms/blog/archive');
    return response.data.data;
  },

  // ─── Search ───

  async search(q: string, options?: {
    page?: number;
    pageSize?: number;
  }): Promise<BlogSearchResponse> {
    const params = new URLSearchParams();
    params.append('q', q);
    if (options?.page) params.append('page', options.page.toString());
    if (options?.pageSize) params.append('pageSize', options.pageSize.toString());

    const response = await apiClient.get<ApiResponse<BlogSearchResponse>>(
      `/cms/blog/search?${params.toString()}`
    );
    return response.data.data;
  },
};

export default blogApi;
